/* eslint-disable @typescript-eslint/no-unused-vars */
import React, { useState, useEffect, useRef, FC, SetStateAction } from 'react'

import Pagination from '@mui/material/Pagination'
import Stack from '@mui/material/Stack'
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward'
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward'
import SearchBar from 'views/components/Searchbar'
import { P } from 'views/components/UI/Text'
import Loader from 'views/components/UI/Loader'
import { StrokedButton } from '../Buttons'
import Tooltip from 'views/components/Tooltip'
import { useSelectedItems } from '../../../screens/Platform-administrator/EvaluatedSpace/context/context'
import { downloadHeader } from 'views/screens/Platform-administrator/EvaluatedSpace/constants'
import { useNotification } from 'lib/context/notification.context'
import TextField from '@mui/material/TextField'
import {
  TableContainer,
  Table,
  TableHeaders,
  TableBody,
  PaginationDiv,
  TableSurface,
  TableToolbar,
  TableToolbarLabel,
  TableToolbarSearch,
  TableMeta,
  TableScrollHint,
  TableRow,
  LoaderContainer,
  ActionsCell,
  TableHeader,
  TableCell,
  NumberCell,
  CheckboxCell,
} from './styled'
import { VALIDATE_EMAIL_REGEX } from 'constants/regex.constants'
import { t } from 'i18next'
import LanguageDropDown from './components/LanguageDropDown'

const ACTIVE_STATUS = 1

// UUID regex pattern to detect UUID format
const UUID_REGEX = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i

type RowDataType = {
  id: string | number
  [key: string]: any
}

type TextWidthRefsType = {
  [key: string]: HTMLSpanElement | null
}

type InputWidthType = {
  [key: string]: string | null
}

interface BluesiteTableProps {
  data: any[]
  getData?: any
  loading?: boolean
  headers: any[]
  sorting?: (a: any, b: any) => number
  searchBar: boolean
  searchLabel: string
  searchFields?: string[]
  labelInput?: string
  viewUrl?: any
  updateUrl?: any
  actions?: any[]
  questionnaireId?: any
  ActionComponent?: React.FC<any>
  extraDataAction?: any
  totalPages?: any
  currentPage?: number
  handlePagination?: (value: number) => void
  limit?: any
  styleSearchBar?: React.CSSProperties
  setNewPage?: (value: number) => void
  page?: number
  setSearchParam?: (value: string) => void
  handleSearchFunction?: (value: string) => void
  checks?: boolean
  hideMainCheckbox?: boolean
  selectedItems?: Set<number>
  setSelectedItems?: React.Dispatch<SetStateAction<Set<number>>>
  setOpenModal?: (value: boolean) => void
  fetchSearchData?: (searchValue: string) => void
  isAlignLeft?: boolean
  onSearch?: (value: string) => void
  renderAdditionalColumn?: (rowData?: any) => React.ReactNode
  editable?: boolean
  onSave?: (rowIndex: number, newData: any) => void
  showNumbers?: boolean
  totalItems?: number
  showTotalItems?: boolean
  itemsPerPage?: number
  languageHeaderOption?: any
  onRowClick?: (rowData: any) => void
  backGroundColor?: string
  disableLanguage?: boolean
  minimumColumnWidth?: number
  tableStyle?:
    | 'inventory'
    | 'process'
    | 'companies'
    | 'companies-management'
    | 'users-management'
    | 'process-users-search'
    | 'process-users-added'
    | 'process-users-360'
    | 'role-family-management'
    | 'development-actions-management'
    | 'skill-questions-management'
    | 'skill-development-actions-management'
    | 'skill-bank-management'
    | 'skill-result-texts-management'
    | 'skills-texts-result'
    | 'process-detail'
    | 'process-detail-users'
}

const BluesiteTable: FC<BluesiteTableProps> = ({
  ActionComponent,
  data,
  searchBar,
  headers,
  searchLabel,
  labelInput,
  extraDataAction,
  currentPage = 1,
  handlePagination = () => {},
  totalPages,
  styleSearchBar,
  loading = false,
  checks,
  hideMainCheckbox,
  selectedItems,
  setSelectedItems,
  setOpenModal,
  setSearchParam,
  onSearch,
  fetchSearchData,
  isAlignLeft,
  renderAdditionalColumn,
  editable = false,
  onSave,
  showNumbers = false,
  totalItems,
  showTotalItems = true,
  itemsPerPage = 10,
  languageHeaderOption,
  onRowClick,
  backGroundColor,
  disableLanguage = false,
  minimumColumnWidth = 220,
  tableStyle,
}) => {
  const [sortedContent, setSortedContent] = useState<any[]>(data?.length > 0 ? data : [])
  const [selectedPage, setSelected] = useState(currentPage)
  const [order, setOrder] = useState('ASC')
  const [showIcon, setShowIcon] = useState(false)
  const [search, setSearch] = useState('')
  const [filteredData, setFilteredData] = useState(data)
  const [editingRow, setEditingRow] = useState<RowDataType | null>(null)
  const [inputWidths, setInputWidths] = useState<InputWidthType>({})
  const [errors, setErrors] = useState<{ [key: string]: boolean }>({})

  const textWidthRefs = useRef<TextWidthRefsType>({})

  const { getWarning } = useNotification()

  const headerCheckboxRef = useRef(null)
  const selectedItemsData = useSelectedItems()
  const isDisabled = selectedItemsData.size > 1
  const actionColumnsCount = (editable ? 1 : 0) + (ActionComponent ? 1 : 0)
  const contentColumnCount = headers.length + actionColumnsCount
  const totalFixedWidth = (checks ? 56 : 0) + (showNumbers ? 72 : 0)
  const distributedColumnWidth =
    contentColumnCount > 0 ? `calc((100% - ${totalFixedWidth}px) / ${contentColumnCount})` : 'auto'
  const tableMinWidth = `${totalFixedWidth + contentColumnCount * minimumColumnWidth}px`

  // Helper function to format document column value
  const formatDocumentValue = (key: string, value: any) => {
    if (key === 'documento' || key === 'document') {
      if (typeof value === 'string' && UUID_REGEX.test(value)) {
        return 'N/A'
      }
    }
    return value
  }

  useEffect(() => {
    if (currentPage !== selectedPage) {
      handlePagination(Number(currentPage))
      setSelected(currentPage)
    }
  }, [currentPage, handlePagination, selectedPage])

  const sorting = (col: string) => {
    if (order === 'ASC') {
      const sorted = [...data].sort((a, b) => (a[col] > b[col] ? 1 : -1))
      setSortedContent(sorted)
      setShowIcon(true)
      setOrder('DSC')
    }
    if (order === 'DSC') {
      const sorted = [...data].sort((a, b) => (a[col] < b[col] ? 1 : -1))
      setSortedContent(sorted)
      setShowIcon(true)
      setOrder('ASC')
    }
  }
  const sortingIcon = () => {
    if (order === 'ASC') {
      return <ArrowUpwardIcon style={{ color: '#2D749C', fontSize: 16 }} />
    } else {
      return <ArrowDownwardIcon style={{ color: '#2D749C', fontSize: 16 }} />
    }
  }

  const handlePaginate = (event: React.ChangeEvent<unknown>, value: number) => {
    handlePagination(value)
    setSelected(value)
  }

  const handleSearch = async () => {
    const searchValue = search.trim().toLowerCase()
    if (fetchSearchData) {
      await fetchSearchData(searchValue)
    } else {
      setFilteredData(data)
    }
    if (searchValue.length === 0) {
      setSearch('')
      setSearchParam?.('')
      setFilteredData(data)
      return
    }
    setSearchParam?.(searchValue)
  }

  const handleSelectItem = (id: number) => {
    if (!setSelectedItems) {
      return
    }
    const newSelectedItems = new Set(selectedItems)
    if (newSelectedItems.has(id)) {
      newSelectedItems.delete(id)
    } else {
      newSelectedItems.add(id)
    }
    setSelectedItems(newSelectedItems)
  }

  const handleSelectAll = (event: any) => {
    if (!setSelectedItems) {
      return
    }
    const checked = event.target.checked
    const newSelectedItems: Set<number> = new Set()

    if (checked) {
      const itemsToSelect = search.length < 1 ? sortedContent : filteredData

      const itemsToSelectFiltered = itemsToSelect.filter((item: any) => {
        if (item.status === ACTIVE_STATUS) {
          return item
        }
        return item.status === itemsToSelect[0].status
      })
      itemsToSelectFiltered.forEach((item) => newSelectedItems.add(item.id))
    }

    setSelectedItems(newSelectedItems)
  }

  useEffect(() => {
    const handleSearch2 = (value: any, fields: string[] = ['name']) => {
      const searchValue = value.trim().toLowerCase()

      const filtered = data.filter((term: any) =>
        fields.some((field) => term[field] && term[field].toLowerCase().includes(searchValue))
      )

      setFilteredData(filtered)
    }

    if (data?.length > 0) {
      handleSearch2(search)
    }
  }, [data, search])

  useEffect(() => {
    if (data?.length > 0) {
      setSortedContent(data)
    }
    if (search.length > 0) {
      setFilteredData(data)
    }
  }, [data, search])

  // #region EDITABLE FIELDS

  useEffect(() => {
    const updateInputWidths = () => {
      const newInputWidths: InputWidthType = {}
      data?.forEach((item) => {
        headers.forEach((header) => {
          const key = `${item.id}-${header.key}`
          const textElement = textWidthRefs.current[key]
          if (textElement) {
            const textWidth = textElement.offsetWidth + 20
            newInputWidths[key] = `${textWidth + 2}px`
          }
        })
      })
      setInputWidths(newInputWidths)
    }
    updateInputWidths()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editingRow])

  const handleEdit = (row: RowDataType) => {
    getWarning(t('global.notifications.editionWarning'))
    setEditingRow({ ...row })
  }

  const handleChange = (key: string, value: string) => {
    let newValue = value

    if (key === 'document') {
      newValue = newValue.replace(/\D/g, '')
    } else if (key === 'email') {
      newValue = newValue.replace(/\s+/g, '')
    } else if (key === 'first_name' || key === 'last_name') {
      newValue = value.replace(/^[ ]+|[0-9]+/g, '')
    } else {
      newValue = newValue.replace(/^\s+/g, '')
    }

    setEditingRow((prevRow: any) => ({ ...prevRow, [key]: newValue }))
  }

  const isValidEmail = (email: string) => {
    if (!email) return false
    const emailRegex = VALIDATE_EMAIL_REGEX
    return emailRegex.test(email)
  }

  const validateRow = (row: RowDataType): boolean => {
    const newRowErrors: { [key: string]: boolean } = {}
    let isValid = true

    headers.forEach((header) => {
      if (header.editable) {
        const value = row[header.key]
        const validator = header.validator ? isValidEmail(value) : !!value

        if (!validator) {
          newRowErrors[header.key] = true
          isValid = false
        }
      }
    })
    setErrors(newRowErrors)
    return isValid
  }

  const handleSave = (rowIndex: number) => {
    if (editingRow && validateRow(editingRow) && onSave) {
      onSave(rowIndex, editingRow)
      setEditingRow(null)
      setErrors({})
    }
  }

  const handleCancel = () => {
    setEditingRow(null)
    setErrors({})
  }

  // #region RETURN

  return (
    <>
      {searchBar === true ? (
        <TableToolbar
          className={
            tableStyle === 'companies' ||
            tableStyle === 'companies-management' ||
            tableStyle === 'users-management' ||
            tableStyle === 'role-family-management' ||
            tableStyle === 'development-actions-management' ||
            tableStyle === 'skill-questions-management' ||
            tableStyle === 'skill-development-actions-management' ||
            tableStyle === 'skill-bank-management' ||
            tableStyle === 'skill-result-texts-management'
              ? 'companies-toolbar'
              : undefined
          }
        >
          <P
            as={TableToolbarLabel}
            style={{ display: isAlignLeft === false ? 'flex' : 'none' }}
            color="#163247"
            fontSize="22px"
          >
            {searchLabel}
          </P>
          <TableToolbarSearch style={styleSearchBar}>
            <SearchBar
              label={labelInput || 'Buscar'}
              value={search}
              onChange={(e) => {
                setSearch(e.target.value)
                onSearch?.(e.target.value)
              }}
              onClick={handleSearch}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  handleSearch()
                }
              }}
              style={{ margin: '0px' }}
            />
          </TableToolbarSearch>
        </TableToolbar>
      ) : (
        ''
      )}
      {showTotalItems && totalItems && <TableMeta>Total de registros: {totalItems}</TableMeta>}
      <TableScrollHint>Desliza para ver mas</TableScrollHint>
      <TableContainer className={tableStyle ? `${tableStyle}-table` : undefined} background={backGroundColor}>
        <TableSurface>
          {!loading ? (
            <Table style={{ minWidth: tableStyle === 'process-detail-users' ? 0 : tableMinWidth }}>
              <colgroup>
                {checks === true ? <col style={{ width: '56px' }} /> : null}
                {showNumbers ? <col style={{ width: '72px' }} /> : null}
                {headers.map((header) => (
                  <col key={header.key} style={{ width: distributedColumnWidth }} />
                ))}
                {editable ? <col style={{ width: distributedColumnWidth }} /> : null}
                {ActionComponent ? <col style={{ width: distributedColumnWidth }} /> : null}
              </colgroup>
              <TableHeaders compact={minimumColumnWidth < 220}>
                <tr>
                  {checks === true ? (
                    <th>
                      {!hideMainCheckbox && (
                        <input
                          type="checkbox"
                          ref={headerCheckboxRef}
                          onChange={handleSelectAll}
                          checked={
                            selectedItems && data ? selectedItems.size === data.length && data.length > 0 : false
                          }
                        />
                      )}
                    </th>
                  ) : null}
                  {showNumbers && <th>#</th>}
                  {headers.map((header, index) => (
                    <TableHeader
                      key={`${header.key}-${index}`}
                      onClick={() => header.name !== 'Idioma' && sorting(header.key)}
                    >
                      {header.renderer === 'StrokedButton' ? (
                        <StrokedButton
                          onClick={() => {
                            if (setOpenModal) {
                              setOpenModal(true)
                            }
                          }}
                        >
                          {header.name}
                        </StrokedButton>
                      ) : (
                        <>
                          {header.name}
                          {showIcon === false ? '' : sortingIcon()}
                          {header.name === 'Idioma' && (
                            <LanguageDropDown disabled={disableLanguage} handleAllLanguages={languageHeaderOption} />
                          )}
                        </>
                      )}
                    </TableHeader>
                  ))}
                  {selectedItems &&
                    selectedItems.size > 1 &&
                    downloadHeader.key === 'download' &&
                    isDisabled &&
                    renderAdditionalColumn &&
                    renderAdditionalColumn()}
                  {editable && <th>Acciones</th>}
                  {ActionComponent && <th>Acciones</th>}
                </tr>
              </TableHeaders>

              {data?.length > 0 ? (
                <>
                  <TableBody>
                    {data?.length > 0 && sortedContent.length > 0 && search.length < 1
                      ? sortedContent?.map((item, index) => {
                          const isEditing = editingRow && editingRow.id === item.id
                          const initialNumber = (currentPage - 1) * itemsPerPage + 1
                          return (
                            <TableRow
                              key={`${item.id}-${index}-${item?.name}`}
                              className={isEditing ? 'isEditing' : selectedItems?.has(item.id) ? 'selected' : ''}
                              onClick={() => onRowClick && onRowClick(item)}
                              style={{ cursor: onRowClick ? 'pointer' : 'default' }}
                            >
                              {checks === true ? (
                                <CheckboxCell data-label="Selección">
                                  <input
                                    type="checkbox"
                                    checked={selectedItems ? selectedItems.has(item.id) : false}
                                    onChange={() => handleSelectItem(item.id)}
                                  />
                                </CheckboxCell>
                              ) : null}
                              {showNumbers && (
                                <NumberCell data-label="#">
                                  {initialNumber + index < 10 ? `0${initialNumber + index}` : initialNumber + index}
                                </NumberCell>
                              )}
                              {headers.map((header) => {
                                const key = `${item.id}-${header.key}`
                                const isEditable = header.editable && editable
                                return (
                                  <TableCell
                                    key={key}
                                    data-label={header.name}
                                    style={{ fontWeight: `${item.isBold ? 'bold' : 'normal'}` }}
                                  >
                                    {header.type === 'checkbox' ? (
                                      <input
                                        type="checkbox"
                                        checked={!!item[header.key]}
                                        disabled={header.isDisabled ? header.isDisabled(item) : false}
                                        onChange={(e) => {
                                          e.stopPropagation()
                                          if (header.onCheckboxChange) {
                                            header.onCheckboxChange(item, !item[header.key])
                                          }
                                        }}
                                      />
                                    ) : header.showDescriptionTooltip ? (
                                      <>
                                        {formatDocumentValue(header.key, item[header.key])}{' '}
                                        <Tooltip
                                          style={{ background: '#2D749C' }}
                                          tooltipText={item[header.description]}
                                        />
                                      </>
                                    ) : isEditable && isEditing ? (
                                      <>
                                        <TextField
                                          size="small"
                                          key={key}
                                          error={!!errors[header.key]}
                                          sx={{
                                            width: '100%',
                                            minWidth: '60px',
                                            maxWidth: '200px',
                                            '& .MuiInputBase-input': {
                                              padding: '8px 10px',
                                              fontSize: 'clamp(0.7rem, 1.5vw, 0.875rem)',
                                            },
                                            '@media (max-width: 1200px)': {
                                              maxWidth: '150px',
                                            },
                                            '@media (max-width: 992px)': {
                                              maxWidth: '120px',
                                              '& .MuiInputBase-input': {
                                                padding: '6px 8px',
                                              },
                                            },
                                            '@media (max-width: 768px)': {
                                              maxWidth: '100px',
                                              '& .MuiInputBase-input': {
                                                padding: '5px 6px',
                                              },
                                            },
                                          }}
                                          value={editingRow && isEditing ? editingRow[header.key] : item[header.key]}
                                          onChange={(e) => handleChange(header.key, e.target.value)}
                                        />
                                        <span
                                          ref={(el) => (textWidthRefs.current[key] = el)}
                                          style={{
                                            position: 'absolute',
                                            visibility: 'hidden',
                                            height: 'auto',
                                            width: 'auto',
                                            whiteSpace: 'nowrap',
                                            fontFamily: 'inherit',
                                            fontSize: 'inherit',
                                            letterSpacing: 'inherit',
                                            padding: 'inherit',
                                          }}
                                        >
                                          {editingRow && isEditing ? editingRow[header.key] : item[header.key]}
                                        </span>
                                      </>
                                    ) : (
                                      header.render ? header.render(item) : formatDocumentValue(header.key, item[header.key])
                                    )}
                                  </TableCell>
                                )
                              })}
                              {editable && (
                                <ActionsCell data-label="Acciones">
                                  {item.edit &&
                                    item.edit(
                                      isEditing,
                                      () => handleEdit(item),
                                      () => handleSave(index),
                                      handleCancel,
                                      index
                                    )}
                                </ActionsCell>
                              )}
                              {ActionComponent && (
                                <ActionsCell data-label="Acciones">
                                  <ActionComponent {...item} {...extraDataAction} />
                                </ActionsCell>
                              )}
                            </TableRow>
                          )
                        })
                      : filteredData?.map((item, index) => {
                          const isEditing = editingRow && editingRow.id === item.id
                          const initialNumber = (currentPage - 1) * itemsPerPage + 1
                          return (
                            <TableRow
                              key={`${item.id}-${index}`}
                              className={isEditing ? 'isEditing' : selectedItems?.has(item.id) ? 'selected' : ''}
                              onClick={() => onRowClick && onRowClick(item)}
                              style={{ cursor: onRowClick ? 'pointer' : 'default' }}
                            >
                              {checks === true ? (
                                <CheckboxCell data-label="Selección">
                                  <input
                                    type="checkbox"
                                    checked={selectedItems ? selectedItems.has(item.id) : false}
                                    onChange={() => handleSelectItem(item.id)}
                                  />
                                </CheckboxCell>
                              ) : null}
                              {showNumbers && (
                                <NumberCell data-label="#">
                                  {initialNumber + index < 10 ? `0${initialNumber + index}` : initialNumber + index}
                                </NumberCell>
                              )}
                              {headers.map((header) => {
                                const key = `${item.id}-${header.key}`
                                const isEditable = header.editable && editable
                                return (
                                  <TableCell key={key} data-label={header.name}>
                                    {header.type === 'checkbox' ? (
                                      <input
                                        type="checkbox"
                                        checked={!!item[header.key]}
                                        disabled={header.isDisabled ? header.isDisabled(item) : false}
                                        onChange={(e) => {
                                          e.stopPropagation()
                                          if (header.onCheckboxChange) {
                                            header.onCheckboxChange(item, !item[header.key])
                                          }
                                        }}
                                      />
                                    ) : isEditable && isEditing ? (
                                      <>
                                        <TextField
                                          size="small"
                                          error={!!errors[header.key]}
                                          sx={{
                                            width: '100%',
                                            minWidth: '60px',
                                            maxWidth: '200px',
                                            '& .MuiInputBase-input': {
                                              padding: '8px 10px',
                                              fontSize: 'clamp(0.7rem, 1.5vw, 0.875rem)',
                                            },
                                            '@media (max-width: 1200px)': {
                                              maxWidth: '150px',
                                            },
                                            '@media (max-width: 992px)': {
                                              maxWidth: '120px',
                                              '& .MuiInputBase-input': {
                                                padding: '6px 8px',
                                              },
                                            },
                                            '@media (max-width: 768px)': {
                                              maxWidth: '100px',
                                              '& .MuiInputBase-input': {
                                                padding: '5px 6px',
                                              },
                                            },
                                          }}
                                          value={editingRow && isEditing ? editingRow[header.key] : item[header.key]}
                                          onChange={(e) => handleChange(header.key, e.target.value)}
                                        />
                                        <span
                                          ref={(el) => (textWidthRefs.current[key] = el)}
                                          style={{
                                            position: 'absolute',
                                            visibility: 'hidden',
                                            height: 'auto',
                                            width: 'auto',
                                            whiteSpace: 'nowrap',
                                            fontFamily: 'inherit',
                                            fontSize: 'inherit',
                                            letterSpacing: 'inherit',
                                            padding: 'inherit',
                                          }}
                                        >
                                          {editingRow && isEditing ? editingRow[header.key] : item[header.key]}
                                        </span>
                                      </>
                                    ) : (
                                      header.render ? header.render(item) : formatDocumentValue(header.key, item[header.key])
                                    )}
                                  </TableCell>
                                )
                              })}
                              {editable && (
                                <ActionsCell data-label="Acciones">
                                  {item.edit &&
                                    item.edit(isEditing, () => handleEdit(item), handleSave, handleCancel, index)}
                                </ActionsCell>
                              )}

                              {ActionComponent && (
                                <ActionsCell data-label="Acciones">
                                  <ActionComponent {...item} {...extraDataAction} />
                                </ActionsCell>
                              )}
                            </TableRow>
                          )
                        })}
                  </TableBody>
                </>
              ) : (
                <TableBody>
                  <TableRow className="empty-state">
                    <TableCell
                      colSpan={
                        headers.length +
                        (checks ? 1 : 0) +
                        (showNumbers ? 1 : 0) +
                        (editable ? 1 : 0) +
                        (ActionComponent ? 1 : 0)
                      }
                    >
                      No hay datos para mostrar
                    </TableCell>
                  </TableRow>
                </TableBody>
              )}
            </Table>
          ) : (
            <LoaderContainer>
              <Loader />
            </LoaderContainer>
          )}
          {totalPages > 1 && !loading ? (
            <PaginationDiv>
              <Stack spacing={2}>
                <Pagination count={totalPages} page={selectedPage} onChange={handlePaginate} color="primary" />
              </Stack>
            </PaginationDiv>
          ) : (
            <></>
          )}
        </TableSurface>
      </TableContainer>
    </>
  )
}

export default BluesiteTable
